import React from 'react'
import { useDebouncedValue } from './useDebouncedValue'

type UseSearchInputOptions = {
  debounceMs?: number
}

type SetDisplayOptions = {
  skipCommit?: boolean
}

type UseSearchInputResult = {
  displayValue: string
  setDisplayValue: (value: string, options?: SetDisplayOptions) => void
  committedValue: string
  setCommittedValue: (value: string) => void
  commitValue: (value: string) => void
}

const useSearchInput = (
  initialValue?: string,
  options?: UseSearchInputOptions,
): UseSearchInputResult => {
  const baseValue = initialValue ?? ''
  const [displayValue, setDisplayState] = React.useState(baseValue)
  const [committedValue, setCommittedState] = React.useState(baseValue)
  const skipSyncRef = React.useRef(false)
  const debouncedDisplay = useDebouncedValue(displayValue, options?.debounceMs ?? 400)

  React.useEffect(() => {
    setDisplayState(baseValue)
    setCommittedState(baseValue)
  }, [baseValue])

  React.useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false
      return
    }
    setCommittedState(debouncedDisplay)
  }, [debouncedDisplay])

  const setDisplayValue = React.useCallback((value: string, setOptions?: SetDisplayOptions) => {
    if (setOptions?.skipCommit) {
      skipSyncRef.current = true
    }
    setDisplayState(value)
  }, [])

  const setCommittedValue = React.useCallback((value: string) => {
    setCommittedState(value)
  }, [])

  const commitValue = React.useCallback((value: string) => {
    setDisplayState(value)
    setCommittedState(value)
  }, [])

  return {
    displayValue,
    setDisplayValue,
    committedValue,
    setCommittedValue,
    commitValue,
  }
}

export type { UseSearchInputOptions, UseSearchInputResult }
export { useSearchInput }
